import {create} from '@github-ui/dotcom-schema/protobuf'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {
  type AssigneesResponse,
  AssigneesResponseSchema,
  type Assignee,
  AssigneeSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import type {LegacyAssignee, LegacyAssigneesData} from '../payloads/assignees'

/**
 * TEMPORARY — proto migration shim for the `assignees` page-data endpoint.
 *
 * The generated `AssigneesResponse` message is the canonical shape that all
 * consumers code against. When the backend `assignees_proto` flag is ON the
 * endpoint returns proto3-JSON, which `queryFnFetch` decodes (via the
 * `X-Proto-JSON` header + the generated `AssigneesResponseSchema`) straight into
 * an `AssigneesResponse` — we pass it through untouched. When the flag is OFF the
 * endpoint returns the legacy JSON shape, which we normalize UP to the proto shape
 * so downstream code only ever sees `AssigneesResponse`.
 *
 * We always normalize legacy → proto (never the other way around). Once the flag
 * is fully rolled out and the legacy backend branch is removed, delete this whole
 * module and pass `AssigneesResponseSchema` to the route directly.
 */

function protoAssignee(assignee: LegacyAssignee): Assignee {
  return create(AssigneeSchema, {
    id: BigInt(assignee.id),
    displayLogin: assignee.displayLogin,
    avatarUrl: assignee.avatarUrl,
  })
}

// Exported for unit testing; the route consumes `assigneesQueryFn` below.
export function normalizeAssignees(data: LegacyAssigneesData): AssigneesResponse {
  return create(AssigneesResponseSchema, {
    assignees: data.assignees.map(protoAssignee),
  })
}

// Proto-decoded messages carry a `$typeName` discriminator; legacy JSON does not.
function isProtoAssignees(response: AssigneesResponse | LegacyAssigneesData): response is AssigneesResponse {
  return '$typeName' in response
}

/**
 * `queryFn` for the `assignees` page-data query. Decodes proto responses against
 * the generated schema and passes them through; normalizes legacy JSON responses
 * up to the proto `AssigneesResponse` shape.
 */
export async function assigneesQueryFn(queryKey: Parameters<typeof queryFnFetch>[0]): Promise<AssigneesResponse> {
  const response = await queryFnFetch<typeof AssigneesResponseSchema, LegacyAssigneesData>(queryKey, {
    schema: AssigneesResponseSchema,
  })
  return isProtoAssignees(response) ? response : normalizeAssignees(response)
}
